// ── /fix — the loose-ends screen ─────────────────────────────────────────────
// Landing page for links inside the "🥞 JOP loose ends" tug event on the JOP
// Assistant calendar. Every open item renders as a card with big one-tap
// fixer buttons. Works two ways: staff login (from /menu) or the ?k= link key
// carried by tug URLs (GCal links open in Safari where the PWA session may
// not exist — the key spares Dan a login at exactly the moment he's acting).
import { useEffect, useState, useCallback } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { STAFF_THEME as T, FONT } from "../../theme/staff";

const KIND_META = {
  hold: { icon: "🎟", label: "Confirm or release?" },
  publish: { icon: "🌐", label: "Put on the website?" },
  detail: { icon: "📍", label: "Missing address" },
};

export default function FixLooseEnds({ navigate }) {
  const k = new URLSearchParams(window.location.search).get("k") || undefined;
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState(null); // {id, text} transient confirmation

  const load = useCallback(async (force = false) => {
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "getLooseEnds");
      const res = await fn({ k, force });
      setItems(res.data.items || []);
    } catch (e) {
      setError(e.message || "Couldn't load loose ends");
      setItems([]);
    }
  }, [k]);

  useEffect(() => { load(); }, [load]);

  // Deep links from the tug event carry #itemId — scroll it into view once loaded.
  useEffect(() => {
    if (!items || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.outline = `2px solid ${T.gold}`;
      setTimeout(() => { el.style.outline = "none"; }, 2500);
    }
  }, [items]);

  async function act(item, action, confirmText, doneText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(item.id);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "resolveLooseEnd");
      const res = await fn({ k, itemId: item.id, action });
      setNote({ id: item.id, text: doneText });
      setTimeout(() => setNote(null), 3000);
      setItems(res.data.items || []);
    } catch (e) {
      setError(e.message || "That didn't work");
    } finally {
      setBusyId(null);
    }
  }

  const btn = (bg, color, solid = true) => ({
    background: solid ? bg : "transparent",
    color,
    border: solid ? "none" : `1px solid ${T.border}`,
    borderRadius: "10px",
    padding: "0.8rem 1rem",
    fontSize: "0.95rem",
    fontWeight: 700,
    fontFamily: FONT.display,
    cursor: "pointer",
    flex: "1 1 auto",
    minWidth: "9rem",
  });

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: FONT.body, padding: "1rem 1rem 3rem" }}>
      <div style={{ maxWidth: "560px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0 1rem" }}>
          <h1 style={{ color: T.gold, fontFamily: FONT.display, fontSize: "1.35rem", fontWeight: 800, margin: 0, flex: 1 }}>
            🥞 Loose ends
          </h1>
          <button onClick={() => load(true)} style={{ ...btn(T.raised, T.muted, false), flex: "0 0 auto", minWidth: 0, padding: "0.5rem 0.8rem", fontSize: "0.8rem" }}>
            ⟳ Refresh
          </button>
          {navigate && (
            <button onClick={() => navigate("/menu")} style={{ ...btn(T.raised, T.muted, false), flex: "0 0 auto", minWidth: 0, padding: "0.5rem 0.8rem", fontSize: "0.8rem" }}>
              ← Menu
            </button>
          )}
        </div>

        {error && (
          <div style={{ background: "rgba(239,83,126,0.15)", border: `1px solid ${T.pink}`, borderRadius: "12px", padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: "0.9rem", lineHeight: 1.45 }}>
            {error}
          </div>
        )}

        {items === null && <div style={{ color: T.muted, padding: "2rem 0", textAlign: "center" }}>Checking the calendar…</div>}

        {items && items.length === 0 && !error && (
          <div style={{ textAlign: "center", padding: "3rem 1rem", color: T.muted }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🎉</div>
            <div style={{ fontFamily: FONT.display, fontSize: "1.1rem", fontWeight: 700, color: T.cream }}>
              No loose ends.
            </div>
            <div style={{ fontSize: "0.9rem", marginTop: "0.35rem" }}>The calendar is clean — nothing needs a decision.</div>
          </div>
        )}

        {(items || []).map((it) => {
          const meta = KIND_META[it.kind] || { icon: "•", label: "" };
          const busy = busyId === it.id;
          return (
            <div key={it.id} id={it.id} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: "16px", padding: "1rem 1.1rem", marginBottom: "1rem", opacity: busy ? 0.6 : 1, transition: "outline 0.3s" }}>
              <div style={{ color: T.muted, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.3rem" }}>
                {meta.icon} {meta.label}
              </div>
              <div style={{ fontFamily: FONT.display, fontSize: "1.05rem", fontWeight: 700, color: T.cream, lineHeight: 1.3 }}>
                {it.title}
              </div>
              <div style={{ color: T.muted, fontSize: "0.85rem", margin: "0.3rem 0 0.9rem" }}>
                {it.dayText}{it.daysOut === 0 ? " — today" : it.daysOut === 1 ? " — tomorrow" : ` — in ${it.daysOut} days`}
                {it.location ? ` · ${it.location.split(",")[0]}` : ""}
              </div>

              {note && note.id === it.id && (
                <div style={{ color: T.gold, fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.6rem" }}>{note.text}</div>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {it.kind === "hold" && (
                  <>
                    <button disabled={busy} onClick={() => act(it, "confirm", null, "✓ Confirmed — HOLD removed from the title")} style={btn(T.gold, T.bg)}>
                      ✓ It's confirmed
                    </button>
                    <button disabled={busy} onClick={() => act(it, "release", `Release the date? "${it.title}" will be deleted from the calendar (recoverable from Google Calendar trash for ~30 days).`, "✗ Released — event deleted")} style={btn(T.raised, T.pink, false)}>
                      ✗ Release the date
                    </button>
                  </>
                )}
                {it.kind === "publish" && (
                  <>
                    {/* Primary: the Event Producer — brief → researched draft →
                        reviewed publish. Carries the link key so the flow
                        works from a Safari-opened GCal link. */}
                    <a
                      href={`/produce?${new URLSearchParams({
                        title: it.title,
                        date: it.day || "",
                        address: it.location || "",
                        eventId: it.eventId,
                        item: it.id,
                        ...(k ? { k } : {}),
                      }).toString()}`}
                      style={{ ...btn(T.gold, T.bg), textDecoration: "none", textAlign: "center" }}
                    >
                      🎬 Produce the listing
                    </a>
                    <button disabled={busy} onClick={() => act(it, "publish", `Publish "${it.title}" to the public calendar as-is — no copy, no links? It'll appear on the website's events page within a couple hours. (The Producer makes a much better listing.)`, "🌐 Published — website updates within ~2h")} style={btn(T.raised, T.muted, false)}>
                      Publish as-is
                    </button>
                    <button disabled={busy} onClick={() => act(it, "dismiss", null, "Okay — never asking about this one again")} style={btn(T.raised, T.muted, false)}>
                      🚫 Not a public event
                    </button>
                  </>
                )}
                {it.kind === "detail" && it.htmlLink && (
                  <a href={it.htmlLink} target="_blank" rel="noreferrer" style={{ ...btn(T.gold, T.bg), textDecoration: "none", textAlign: "center" }}>
                    📍 Add the address in Google Calendar
                  </a>
                )}
                <button disabled={busy} onClick={() => act(it, "snooze", null, "💤 Snoozed — I'll ask again in a week")} style={btn(T.raised, T.muted, false)}>
                  💤 Ask me in a week
                </button>
                {it.kind === "detail" && (
                  <button disabled={busy} onClick={() => act(it, "dismiss", null, "Okay — never asking about this one again")} style={btn(T.raised, T.muted, false)}>
                    🚫 Leave it be
                  </button>
                )}
              </div>

              {it.htmlLink && it.kind !== "detail" && (
                <a href={it.htmlLink} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginTop: "0.7rem", color: T.sky, fontSize: "0.8rem", textDecoration: "none" }}>
                  Open in Google Calendar →
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
